import * as React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Loader2,
  User,
  Pencil,
  Clock,
  Users,
  Plus,
  MoreHorizontal,
  ChevronRight,
  Shield,
  UserCheck,
  UserX,
} from 'lucide-react';
import fetchProfile from '../../../application/routes/profile/fetchProfile';
import { usePermissions } from '../../../application/context/PermissionsContext/PermissionsContext';
import { PermissionKeys } from '../../../application/context/PermissionsContext/permissionTypes';
import {
  selectAllProfiles,
  loadProfileAction,
} from '../../../application/store/reducers/users';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { useToast } from '../../components/ui/toast';
import fetchProfileWithTeams from '../../../application/routes/profile/fetchProfileWithTeams';
import {
  PersonProfileCard,
  PersonProfileValues,
} from '../../components/person-profile-card';
import { WorkingHoursModal } from './components/WorkingHoursModal';
import { ChangeRoleModal } from './components/ChangeRoleModal';
import { isStarterAccount } from '../../../application/common/utils/isStarterAccount';
import { canChangeStatus, useUserStatus } from '../../hooks/use-user-status';
import { ConfirmModal } from '../../components/ui/confirm-modal';

declare const global: {
  me: {
    id: string;
    orgID: string;
    imageName?: string;
    name: string;
  };
};

export function PersonDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { hasPermission, checkPermission } = usePermissions();
  const toast = useToast();

  const reduxProfiles = useSelector(selectAllProfiles);
  const reduxProfile =
    userId && reduxProfiles[userId] ? reduxProfiles[userId] : null;

  const [profile, setProfile] = useState<any>(reduxProfile?.user || null);
  const [isLoading, setIsLoading] = useState(!profile);
  const [teams, setTeams] = useState<any[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);

  const showEdit = userId
    ? hasPermission(PermissionKeys.TeamsPeople_UpdateUser, [userId])
    : false;
  const showCalendar = userId
    ? hasPermission(PermissionKeys.Scheduling_ViewAppointments, [userId])
    : false;
  const showAddToTeam =
    userId && !isStarterAccount()
      ? hasPermission(PermissionKeys.TeamsPeople_AddPersonToTeam, [userId])
      : false;
  const showRemoveFromTeam = userId
    ? hasPermission(PermissionKeys.TeamsPeople_RemoveSomeoneFromTeam, [userId])
    : false;
  const showChangeRole = checkPermission(
    PermissionKeys.TeamsPeople_ChangePersonsRole
  );
  const showChangeStatus = checkPermission(
    PermissionKeys.TeamsPeople_ChangePersonsStatus
  );
  const userStatus = useUserStatus();

  useEffect(() => {
    if (reduxProfile?.user) {
      setProfile(reduxProfile.user);
      setIsLoading(false);
    } else {
      loadProfile();
    }
    loadTeams();
  }, [userId]);

  async function loadProfile() {
    if (!userId) {
      return;
    }
    setIsLoading(true);
    try {
      const user = await fetchProfile(userId);
      if (user) {
        setProfile(user);
        dispatch(loadProfileAction({ user } as any));
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
    } finally {
      setIsLoading(false);
    }
  }

  async function loadTeams() {
    if (!userId) {
      return;
    }
    setTeamsLoading(true);
    try {
      const user = await fetchProfileWithTeams(userId);
      if (user?.circles?.items) {
        setTeams(user.circles.items);
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
    } finally {
      setTeamsLoading(false);
    }
  }

  const handleRemoveFromTeam = async (circleID: string) => {
    try {
      const { API } = await import('aws-amplify');
      const { modifyCircleMember } = await import('../../../graphql/mutations');
      await API.graphql({
        query: modifyCircleMember,
        variables: {
          circleID,
          personID: userId,
          status: 'removed',
          role: '',
        },
      });
      setTeams((prev) => prev.filter((t) => t.circleID !== circleID));
      toast.success('Removed from team');
    } catch (err) {
      console.error('Error removing from team:', err);
      toast.error('Failed to remove from team');
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-2 px-6 lg:px-10 py-5 border-b border-border">
          <span className="text-base font-bold text-[hsl(var(--primary-dark))]">
            People
          </span>
          <ChevronRight className="size-4 text-muted-foreground" />
          <span className="text-base font-bold text-foreground">
            Loading...
          </span>
        </div>
        <div className="flex items-center justify-center flex-1">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-2 px-6 lg:px-10 py-5 border-b border-border">
          <button
            onClick={() => navigate('/v2/people')}
            className="text-base font-bold text-[hsl(var(--primary-dark))] hover:opacity-80 transition-opacity"
          >
            People
          </button>
          <ChevronRight className="size-4 text-muted-foreground" />
          <span className="text-base font-bold text-foreground">Not Found</span>
        </div>
        <div className="flex flex-col items-center justify-center flex-1 text-muted-foreground">
          <User className="size-12 mb-3" />
          <p className="text-sm">This person could not be found.</p>
        </div>
      </div>
    );
  }

  const profileValues: PersonProfileValues = {
    id: profile.id || '',
    name: profile.name || '',
    email: profile.email || '',
    phoneNumber: profile.phoneNumber || '',
    title: profile.title || '',
    location: profile.location || '',
    imageName: profile.imageName || '',
    imageType: profile.imageType || '',
    avatarFile: profile.avatarFile || '',
    initials: profile.initials || '',
    functionalRoles: Array.isArray(profile.functionalRoles)
      ? profile.functionalRoles.join('_')
      : profile.functionalRoles || '',
  };

  return (
    <div className="flex flex-col h-full">
      {/* Breadcrumb Header */}
      <div className="flex items-center gap-2 px-4 sm:px-6 lg:px-10 py-4 sm:py-5 border-b border-border bg-background shrink-0">
        <button
          onClick={() => navigate('/v2/people')}
          className="text-base font-bold text-[hsl(var(--primary-dark))] hover:opacity-80 transition-opacity"
        >
          People
        </button>
        <ChevronRight className="size-4 text-muted-foreground" />
        <span className="text-base font-bold text-foreground">
          {profile.name}
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[976px] mx-auto px-4 sm:px-6 lg:px-10 py-6">
          {/* Profile Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-10">
            <div className="flex items-center gap-4 sm:gap-6">
              {/* Avatar with edit badge */}
              <div className="relative shrink-0">
                {profile.avatarFile ? (
                  <img
                    src={profile.avatarFile}
                    alt={profile.name}
                    className="size-16 sm:size-[100px] rounded-full object-cover bg-muted"
                  />
                ) : (
                  <div className="size-16 sm:size-[100px] rounded-full bg-muted flex items-center justify-center">
                    <span className="text-lg sm:text-2xl font-bold text-muted-foreground">
                      {profile.initials ||
                        profile.name?.charAt(0)?.toUpperCase()}
                    </span>
                  </div>
                )}
                {showEdit && (
                  <button
                    onClick={() => navigate(`/v2/people/edit/${userId}`)}
                    className="absolute bottom-0 right-0 size-7 sm:size-10 rounded-full bg-background border border-border flex items-center justify-center hover:bg-muted transition-colors"
                  >
                    <Pencil className="size-3 sm:size-4 text-foreground" />
                  </button>
                )}
              </div>

              {/* Name + Email */}
              <div>
                <h1 className="font-serif text-2xl font-normal leading-tight tracking-[-0.02em] text-foreground">
                  {profile.name}
                </h1>
                <p className="text-sm text-muted-foreground">{profile.email}</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 flex-wrap">
              {showChangeStatus &&
                canChangeStatus(profile.status) &&
                userId !== global.me.id && (
                  <Button
                    variant="outline"
                    className="h-10 sm:h-12 px-3 sm:px-4 rounded-lg gap-2 sm:gap-2.5 text-sm sm:text-base font-semibold"
                    disabled={userStatus.isBusy}
                    onClick={() =>
                      userStatus.request(
                        profile,
                        profile.status === 'active' ? 'disable' : 'enable',
                        (status) =>
                          setProfile((prev: any) => ({ ...prev, status }))
                      )
                    }
                  >
                    {userStatus.isBusy ? (
                      <Loader2 className="size-4 sm:size-5 animate-spin" />
                    ) : profile.status === 'active' ? (
                      <UserX className="size-4 sm:size-5" />
                    ) : (
                      <UserCheck className="size-4 sm:size-5" />
                    )}
                    {profile.status === 'active' ? 'Deactivate' : 'Activate'}
                  </Button>
                )}
              {showCalendar && (
                <Button
                  variant="outline"
                  className="h-10 sm:h-12 px-3 sm:px-4 rounded-lg gap-2 sm:gap-2.5 text-sm sm:text-base font-semibold"
                  onClick={() => setShowAvailabilityModal(true)}
                >
                  <Clock className="size-4 sm:size-5" />
                  Availability
                </Button>
              )}
              {showChangeRole && (
                <Button
                  variant="outline"
                  className="h-10 sm:h-12 px-3 sm:px-4 rounded-lg gap-2 sm:gap-2.5 text-sm sm:text-base font-semibold"
                  onClick={() => setShowRoleModal(true)}
                >
                  <Shield className="size-4 sm:size-5" />
                  Change Role
                </Button>
              )}
              {showEdit && (
                <Button
                  variant="outline"
                  className="h-10 sm:h-12 px-3 sm:px-4 rounded-lg gap-2 sm:gap-2.5 text-sm sm:text-base font-semibold"
                  onClick={() => navigate(`/v2/people/edit/${userId}`)}
                >
                  <Pencil className="size-4 sm:size-5" />
                  Edit
                </Button>
              )}
            </div>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Contact Details Card */}
            <PersonProfileCard
              mode="view"
              values={profileValues}
              className="h-full"
            />

            {/* Teams Card */}
            <div className="bg-background border border-border/60 rounded-xl p-6 h-full flex flex-col">
              <h3 className="text-base font-bold text-foreground mb-4">
                Teams
              </h3>

              {teamsLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <Loader2 className="size-6 animate-spin text-muted-foreground" />
                </div>
              ) : teams.length === 0 ? (
                <div className="flex-1 bg-muted rounded-3xl flex flex-col items-center justify-center gap-6 py-12">
                  <div className="flex flex-col items-center gap-2.5">
                    <Users className="size-6 text-foreground" />
                    <p className="text-base font-semibold text-foreground">
                      Not part of any team yet
                    </p>
                  </div>
                  {showAddToTeam && (
                    <Button
                      variant="outline"
                      className="h-12 px-4 rounded-lg gap-2.5 text-base font-semibold bg-background"
                      onClick={() => setShowAddTeamModal(true)}
                    >
                      <Plus className="size-5" />
                      Add to team
                    </Button>
                  )}
                </div>
              ) : (
                <div className="flex-1 space-y-2">
                  {teams.map((team) => (
                    <div
                      key={team.circleID}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() =>
                        navigate(`/teams/${team.circleID}`, {
                          state: { profileAboutPage: true, userId },
                        })
                      }
                    >
                      {team.circle?.avatarFile ? (
                        <img
                          src={team.circle.avatarFile}
                          alt={team.circle.name}
                          className="size-9 rounded-full object-cover"
                        />
                      ) : (
                        <div className="size-9 rounded-full bg-muted flex items-center justify-center">
                          <Users className="size-4 text-muted-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {team.circle?.name}
                        </p>
                        {team.memberType && team.memberType !== 'member' && (
                          <span className="text-xs text-muted-foreground capitalize">
                            {team.memberType}
                          </span>
                        )}
                      </div>
                      {showRemoveFromTeam && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              onClick={(e) => e.stopPropagation()}
                              className="size-8 flex items-center justify-center rounded-md hover:bg-muted transition-colors shrink-0"
                            >
                              <MoreHorizontal className="size-4 text-muted-foreground" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveFromTeam(team.circleID);
                              }}
                            >
                              Remove from Team
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  ))}

                  {showAddToTeam && (
                    <Button
                      variant="outline"
                      className="w-full h-12 rounded-lg gap-2.5 text-base font-semibold mt-4"
                      onClick={() => setShowAddTeamModal(true)}
                    >
                      <Plus className="size-5" />
                      Add to team
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Add to Team Modal */}
      {showAddTeamModal && (
        <AddTeamModalWrapper
          userId={userId!}
          currentTeams={teams}
          onClose={() => setShowAddTeamModal(false)}
          onTeamsAdded={(newTeams: any[]) => {
            setTeams(newTeams);
            setShowAddTeamModal(false);
          }}
        />
      )}

      {/* Working Hours Modal */}
      <WorkingHoursModal
        open={showAvailabilityModal}
        onClose={() => setShowAvailabilityModal(false)}
        userID={userId || ''}
      />

      {/* Activate / Deactivate Confirmation */}
      <ConfirmModal {...userStatus.confirmProps} />

      {/* Change Role Modal */}
      <ChangeRoleModal
        open={showRoleModal}
        onClose={() => setShowRoleModal(false)}
        orgMemberID={profile?.orgs?.items?.[0]?.id || ''}
        currentRole={
          profile?.memberType ||
          profile?.orgs?.items?.[0]?.memberType ||
          'member'
        }
        userName={profile?.name || ''}
        onRoleChanged={(newRole) => {
          setProfile((prev: any) => ({
            ...prev,
            memberType: newRole,
            orgs: {
              ...prev?.orgs,
              items: prev?.orgs?.items?.map((item: any, i: number) =>
                i === 0 ? { ...item, memberType: newRole } : item
              ),
            },
          }));
        }}
      />
    </div>
  );
}

// ── Add Team Modal Wrapper ──────────────────────────────────

const AddMemberLinkTeamModal = React.lazy(
  () => import('../../../application/routes/teams/AddMemberLinkTeamModal')
);

function AddTeamModalWrapper({
  userId,
  currentTeams,
  onClose,
  onTeamsAdded,
}: {
  userId: string;
  currentTeams: any[];
  onClose: () => void;
  onTeamsAdded: (teams: any[]) => void;
}) {
  const handleSubmit = async (newTeams: any[]) => {
    try {
      const { API } = await import('aws-amplify');
      const { modifyCircleMember } = await import('../../../graphql/mutations');

      const currentIds = new Set(currentTeams.map((t) => t.circleID));
      const teamsToAdd = newTeams.filter((t) => !currentIds.has(t.id));

      for (const team of teamsToAdd) {
        await API.graphql({
          query: modifyCircleMember,
          variables: {
            circleID: team.id,
            personID: userId,
            status: 'accepted',
            role: '',
          },
        });
      }

      const fetchProfileWithTeamsModule = await import(
        '../../../application/routes/profile/fetchProfileWithTeams'
      );
      const user = await fetchProfileWithTeamsModule.default(userId);
      if (user?.circles?.items) {
        onTeamsAdded(user.circles.items);
      }
    } catch (err) {
      console.error('Error adding to teams:', err);
      onClose();
    }
  };

  return (
    <React.Suspense fallback={null}>
      <AddMemberLinkTeamModal
        title="Add to Teams"
        team={null}
        teamLevelNumIdMap={null}
        teamLevelNumNameMap={null}
        noAutoSelect={true}
        pickCircles={true}
        pickUsers={false}
        update={null}
        filter={(global as any).me.orgID}
        itemValue=""
        onSubmit={handleSubmit}
        hideInitialChecked
        initialActiveMembers={currentTeams.map((t) => ({
          id: t.circleID,
          name: t.circle?.name,
        }))}
        setShowModal={onClose}
        showModal={true}
      />
    </React.Suspense>
  );
}
